import { useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { selectSafetyEvents } from '@store/selectors/monitor.selectors';
import styles from './LiveButton.module.css';

interface ILiveButtonProps {
  /** The bar is at the live edge. */
  live: boolean;
  onClick(): void;
}

/**
 * The bar's LIVE button. Away from live (the rail shows a past minute), it
 * counts the live safety alerts raised since: "LIVE · 2 new alerts".
 */
export function LiveButton({ live, onClick }: ILiveButtonProps) {
  return live ? <LiveButtonView live newAlerts={0} onClick={onClick} /> : <AwayFromLive onClick={onClick} />;
}

/** Mounts when the bar leaves live, so its mount time is when the user left. */
function AwayFromLive({ onClick }: Pick<ILiveButtonProps, 'onClick'>) {
  const [since] = useState(Date.now);
  // One selector per mount: it recounts when the safety events change, never per playback tick.
  const selectNewAlerts = useMemo(
    () => createSelector([selectSafetyEvents], (events) => events.filter((event) => event.timestampCreated >= since).length),
    [since],
  );
  const newAlerts = useAppSelector(selectNewAlerts);
  return <LiveButtonView live={false} newAlerts={newAlerts} onClick={onClick} />;
}

function LiveButtonView({ live, newAlerts, onClick }: ILiveButtonProps & { newAlerts: number }) {
  const { t } = useTranslation();
  const alerting = newAlerts > 0;
  return (
    <button
      type="button"
      className={alerting ? `${styles.live} ${styles.liveAlerting}` : styles.live}
      aria-pressed={live}
      onClick={onClick}
    >
      <i aria-hidden className={live || alerting ? styles.dotLive : styles.dot} />
      {t('monitor.replay.live')}
      {alerting && <span>· {t('monitor.replay.newAlerts', { count: newAlerts })}</span>}
    </button>
  );
}
